import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  ChevronDown, 
  ChevronUp, 
  Code, 
  User, 
  Database,
  Zap
} from "lucide-react";

/**
 * DevOverlay - Development-only debugging panel
 * Only renders in development mode
 */
export default function DevOverlay() {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();

  // Only render in development
  if (import.meta.env.MODE === "production") {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-[9999]">
      {/* Toggle Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className="rounded-full shadow-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
        size="lg"
      >
        <Code className="w-5 h-5 mr-2" />
        Dev Tools
        {isOpen ? <ChevronDown className="w-4 h-4 ml-2" /> : <ChevronUp className="w-4 h-4 ml-2" />}
      </Button>

      {/* Dev Panel */}
      {isOpen && (
        <Card className="absolute bottom-16 right-0 w-96 max-h-[600px] overflow-y-auto shadow-2xl">
          <div className="p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="text-lg font-bold">Development Tools</h3>
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                DEV MODE
              </Badge>
            </div>

            {/* Environment Info */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Zap className="w-4 h-4 text-primary" />
                <span>Environment</span>
              </div>
              <div className="pl-6 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Mode:</span>
                  <span className="font-mono">{import.meta.env.MODE}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Vite:</span>
                  <Badge variant="secondary" className="text-xs">Active</Badge>
                </div>
              </div>
            </div>

            {/* Auth Info */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <User className="w-4 h-4 text-primary" />
                <span>Authentication</span>
              </div>
              <div className="pl-6 space-y-1 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Status:</span>
                  <Badge variant={user ? "default" : "destructive"} className="text-xs">
                    {user ? "Authenticated" : "Not Logged In"}
                  </Badge>
                </div>
                {user && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">User ID:</span>
                      <span className="font-mono text-[10px]">{user.id.slice(0, 8)}...</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Email:</span>
                      <span className="font-mono text-[10px]">{user.email}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Database Info */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Database className="w-4 h-4 text-primary" />
                <span>Backend</span>
              </div>
              <div className="pl-6 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Supabase:</span>
                  <Badge variant="default" className="text-xs bg-green-600">Connected</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Project ID:</span>
                  <span className="font-mono text-[10px]">
                    {import.meta.env.VITE_SUPABASE_PROJECT_ID || "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-2 border-t pt-3">
              <div className="text-sm font-medium">Quick Actions</div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.href = "/admin/questions"}
                  className="text-xs"
                >
                  Admin Panel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => console.log({ user, env: import.meta.env })}
                  className="text-xs"
                >
                  Log State
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => localStorage.clear()}
                  className="text-xs"
                >
                  Clear Cache
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.reload()}
                  className="text-xs"
                >
                  Reload
                </Button>
              </div>
            </div>

            {/* Tips */}
            <div className="bg-muted rounded-lg p-3 text-xs">
              <div className="font-medium mb-1">💡 Dev Tips:</div>
              <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                <li>Press F12 to open browser DevTools</li>
                <li>Check Network tab for API calls</li>
                <li>Use Console for debugging</li>
              </ul>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
